import type { RowData, TableOptions } from '@tanstack/react-table';

import {
  columnFilteringFeature,
  columnVisibilityFeature,
  constructFilterFn,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  createTableHook,
  filterFn_arrIncludesSome,
  filterFn_equals,
  filterFn_includesString,
  globalFilteringFeature,
  metaHelper,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import type { ColumnMeta } from './Table.types';

import { Table } from './Table';
import { tableContext } from './TableRoot.context';

// Custom filter function for multi select and multi combobox
const equalsOneOf = constructFilterFn({
  filter: (dataValue: unknown, filterValue: unknown[]) => filterValue.includes(dataValue),
  autoRemove: (value: unknown) => !Array.isArray(value) || value.length === 0,
});

const features = tableFeatures({
  columnFilteringFeature,
  globalFilteringFeature,
  rowSortingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  columnVisibilityFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  filterFns: {
    arrIncludesSome: filterFn_arrIncludesSome,
    equals: filterFn_equals,
    equalsOneOf,
    includesString: filterFn_includesString,
  },
  // The sort functions `sortFn: 'auto'` can resolve to, besides the always-available `basic`
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
  columnMeta: metaHelper<ColumnMeta>(),
});

export type AppTableFeatures = typeof features;

const { createAppColumnHelper, useAppTable } = createTableHook({
  features,
  // The table components read this context, which lives in a separate module so they don't have to import this one
  tableContext,
  defaultColumn: {
    filterFn: 'equals', // Use strict equals comparing `===` as default
  },
  getRowId: ({ id }: { id: number }) => `${id}`,
  globalFilterFn: 'includesString',
  enableHiding: false, // Columns are hidden through the column visibility state, never by the user
});

export { createAppColumnHelper };

export type AppTable<TData extends RowData> = ReturnType<typeof useAppTable<TData>>;

export const useTable = <TData extends RowData & { id: number }>(options: Omit<TableOptions<AppTableFeatures, TData>, 'features'>) => {
  const table = useAppTable(options);

  /**
   * Table components are exposed from this hook to enable strong typing, by passing the inferred data type to these components (where applicable).
   *
   * Note that this does not enforce correctness, as it is possible to mix the components of multiple `useTable` instances.\
   * The typing of these components only exist for convenience — it is the consumer's responsibility to use these correctly.
   */
  const components = useMemo(
    () => ({
      ...Table,
      Root: Table.Root<TData>,
      Body: Table.Body<TData>,
      Row: Table.Row<TData>,
    }),
    [],
  );

  return [table, components] as const;
};
